import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/lib/auth-context";
import api from "@/lib/api";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "@/components/ui/sheet";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import ConnectorIcon from "@/components/data/ConnectorIcon";
import { PrebuiltSourcesView } from "./PrebuiltSourcesView";
import { Database, Layers, PlugZap, ExternalLink, RefreshCw, FileSpreadsheet, AlertOctagon, Gauge, GitBranch } from "lucide-react";

/**
 * Drawer listing every source feeding a dashboard. For dataset-bound
 * dashboards = single dataset; for unified / template-bound = canonical
 * models + connectors writing into them.
 *
 * Uses GET /api/dashboards/{id}/sources — no source-type hardcoding here.
 */
export default function DashboardSourcesDrawer({ dashboardId, open, onClose, prebuilt = false }) {
    const navigate = useNavigate();
    const { user } = useAuth();
    const isSuperAdmin = user?.role === "super_admin";
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(false);
    useEffect(() => {
        if (!open || !dashboardId) return;
        setLoading(true);
        api.get(`/dashboards/${dashboardId}/sources`)
            .then((r) => setData(r.data))
            .catch(() => setData({ datasets: [], models: [], connectors: [] }))
            .finally(() => setLoading(false));
    }, [open, dashboardId]);

    const totalSources = (data?.datasets?.length || 0) + (data?.connectors?.length || 0);
    const impact = data?.impact || {};
    const impactAny = (impact.widgets || 0) + (impact.kpis || 0) > 0;

    return (
        <Sheet open={open} onOpenChange={(o) => { if (!o) onClose(); }}>
            <SheetContent className="w-full sm:max-w-lg overflow-y-auto" data-testid="dashboard-sources-drawer">
                <SheetHeader>
                    <SheetTitle className="flex items-center gap-2">
                        <Database className="h-4 w-4 text-primary" /> {prebuilt ? "Data powering this dashboard" : "Sources powering this dashboard"}
                    </SheetTitle>
                    <SheetDescription>
                        {loading ? "Loading…" : prebuilt
                            ? `${(data?.models || []).length} data model${(data?.models || []).length === 1 ? "" : "s"} · ${(data?.connectors || []).length} connector${(data?.connectors || []).length === 1 ? "" : "s"}`
                            : `${totalSources} source${totalSources === 1 ? "" : "s"} feed this view.`}
                    </SheetDescription>
                </SheetHeader>

                {loading ? (
                    <div className="py-12 text-center text-sm text-muted-foreground"><RefreshCw className="h-4 w-4 inline mr-2 animate-spin" />Resolving sources…</div>
                ) : prebuilt ? (
                    <PrebuiltSourcesView data={data} isSuperAdmin={isSuperAdmin} onClose={onClose} />
                ) : (
                    <div className="space-y-6 mt-6">
                        {/* Reverse impact — the mirror of the dataset lineage
                            impact banner. Tells the user how many widgets in
                            THIS dashboard consume the upstream data. */}
                        <div
                            className={`border rounded-sm p-3 flex items-start gap-3 ${
                                impactAny
                                    ? "border-primary/40 bg-primary/5"
                                    : "border-border bg-muted/20"
                            }`}
                            data-testid="dashboard-sources-impact"
                        >
                            <div className={`h-8 w-8 rounded-sm flex items-center justify-center shrink-0 ${
                                impactAny ? "bg-primary/15 text-primary"
                                          : "bg-muted text-muted-foreground"
                            }`}>
                                <AlertOctagon className="h-4 w-4" />
                            </div>
                            <div className="text-sm">
                                <div className="font-medium mb-1">
                                    {impactAny ? "This dashboard depends on…" : "No upstream data"}
                                </div>
                                <div className="text-muted-foreground text-xs">
                                    {impactAny ? (
                                        <>
                                            <b>{impact.datasets || 0}</b> dataset{impact.datasets === 1 ? "" : "s"}{" "}
                                            {(impact.connectors || 0) > 0 && (
                                                <>· <b>{impact.connectors}</b> live source{impact.connectors === 1 ? "" : "s"} </>
                                            )}
                                            powering <b>{impact.widgets || 0}</b> widget{impact.widgets === 1 ? "" : "s"}
                                            {(impact.kpis || 0) > 0 && <> (<b>{impact.kpis}</b> KPI{impact.kpis === 1 ? "" : "s"})</>}.
                                            Break any of them and this dashboard breaks with it.
                                        </>
                                    ) : (
                                        <>Nothing upstream is currently feeding this dashboard.</>
                                    )}
                                </div>
                            </div>
                        </div>
                        {/* Datasets (single-source dashboards) */}
                        {(data?.datasets || []).length > 0 && (
                            <section data-testid="sources-datasets">
                                <div className="eyebrow mb-2 flex items-center gap-1.5"><FileSpreadsheet className="h-3 w-3" /> Dataset</div>
                                {data.datasets.map((ds) => (
                                    <div key={ds.id} className="border border-border rounded-sm bg-card px-3 py-2 mb-2 space-y-1.5" data-testid={`sources-dataset-${ds.id}`}>
                                        <div className="flex items-center justify-between gap-3">
                                            <div className="min-w-0">
                                                <div className="font-medium text-sm truncate">{ds.name}</div>
                                                <div className="text-[11px] font-mono text-muted-foreground truncate">
                                                    {ds.source_type} · {ds.row_count?.toLocaleString() || 0} rows · {ds.column_count || 0} cols
                                                    {ds.last_loaded_at && ` · loaded ${new Date(ds.last_loaded_at).toLocaleString()}`}
                                                </div>
                                            </div>
                                            <Button variant="ghost" size="sm" className="rounded-sm h-7 text-xs"
                                                onClick={() => { onClose(); navigate(`/data?tab=datasets&dataset=${ds.id}`); }}
                                                data-testid={`sources-dataset-open-${ds.id}`}>
                                                Open <ExternalLink className="h-3 w-3 ml-1" />
                                            </Button>
                                        </div>
                                        {/* Mapping summary. The technical
                                            "model.<slug>" badge is SA-only —
                                            end users just see how many rows
                                            landed and whether it was auto or
                                            manual. */}
                                        {ds.mapping ? (
                                            <div className="border-t border-border pt-1.5 flex items-center justify-between gap-2">
                                                <div className="flex items-center gap-1.5 flex-wrap">
                                                    {isSuperAdmin && ds.mapping.model_slug && (
                                                        <Badge variant="outline" className="font-mono text-[9px]">
                                                            {ds.mapping.model_slug}
                                                        </Badge>
                                                    )}
                                                    <Badge variant="outline" className="font-mono text-[9px] border-[hsl(var(--success))] text-[hsl(var(--success))]">{ds.mapping.rows_mapped?.toLocaleString() || 0} mapped</Badge>
                                                    <Badge variant={ds.mapping.source === "manual" ? "default" : "outline"} className="font-mono text-[9px]">
                                                        {ds.mapping.source === "manual" ? "manual remap" : "auto-matched"}
                                                    </Badge>
                                                    <span className="text-[10px] text-muted-foreground font-mono">{Object.keys(ds.mapping.field_map || {}).length} fields</span>
                                                </div>
                                                <Button variant="ghost" size="sm" className="rounded-sm h-6 text-[10px] px-2"
                                                    onClick={() => { onClose(); navigate(`/data?tab=datasets&dataset=${ds.id}&dataset_tab=mapping`); }}
                                                    data-testid={`sources-dataset-remap-${ds.id}`}>
                                                    Remap
                                                </Button>
                                            </div>
                                        ) : (
                                            <div className="border-t border-border pt-1.5 text-[10px] font-mono text-amber-600">
                                                No canonical mapping yet — open the dataset to map columns.
                                            </div>
                                        )}
                                    </div>
                                ))}
                            </section>
                        )}
                        {/* Integrated data layer — SA only. Product decision:
                            end users must never see the technical "model"
                            surface; call it "Integrated data layer" here to
                            match the dataset lineage node label. */}
                        {isSuperAdmin && (data?.models || []).length > 0 && (
                            <section data-testid="sources-data-layer">
                                <div className="eyebrow mb-2 flex items-center gap-1.5"><GitBranch className="h-3 w-3" /> Integrated data layer</div>
                                {data.models.map((m) => (
                                    <div key={m.id} className="border border-dashed border-border rounded-sm bg-muted/10 px-3 py-2 mb-2 flex items-center justify-between gap-3">
                                        <div className="min-w-0">
                                            <div className="font-medium text-sm truncate">{m.name}</div>
                                            <div className="text-[11px] font-mono text-muted-foreground truncate">
                                                {m.row_count?.toLocaleString() || 0} rows
                                            </div>
                                        </div>
                                        <Button variant="ghost" size="sm" className="rounded-sm h-7 text-xs"
                                            onClick={() => { onClose(); navigate(`/data-models?model=${m.slug}`); }}
                                            data-testid={`sources-model-open-${m.slug}`}>
                                            Open <ExternalLink className="h-3 w-3 ml-1" />
                                        </Button>
                                    </div>
                                ))}
                            </section>
                        )}
                        {/* Connectors — the live sources feeding the pipeline. */}
                        {(data?.connectors || []).length > 0 && (
                            <section data-testid="sources-connectors">
                                <div className="eyebrow mb-2 flex items-center gap-1.5"><PlugZap className="h-3 w-3" /> Live sources</div>
                                {data.connectors.map((c) => (
                                    <div key={c.id} className="border border-border rounded-sm bg-card px-3 py-2 mb-2 flex items-center justify-between gap-3">
                                        <div className="flex items-center gap-2 min-w-0">
                                            <div className="w-7 h-7 bg-secondary rounded-sm flex items-center justify-center overflow-hidden p-1 shrink-0">
                                                <ConnectorIcon iconUrl={c.icon_url} category={c.category} name={c.name} className="max-w-full max-h-full" />
                                            </div>
                                            <div className="min-w-0">
                                                <div className="font-medium text-sm truncate">{c.name}</div>
                                                <div className="text-[11px] font-mono text-muted-foreground truncate">
                                                    {c.connector_key}
                                                    {c.last_sync_at && ` · synced ${new Date(c.last_sync_at).toLocaleString()}`}
                                                </div>
                                            </div>
                                        </div>
                                        <Badge variant="outline" className={`font-mono text-[9px] ${c.last_sync_status === "success" ? "border-[hsl(var(--success))] text-[hsl(var(--success))]" : "border-amber-500 text-amber-600"}`}>
                                            {c.last_sync_status || "—"}
                                        </Badge>
                                    </div>
                                ))}
                            </section>
                        )}
                        {totalSources === 0 && (!isSuperAdmin || (data?.models || []).length === 0) && (
                            <div className="py-12 text-center text-sm text-muted-foreground" data-testid="sources-empty">
                                No sources currently feed this dashboard.
                            </div>
                        )}
                    </div>
                )}
            </SheetContent>
        </Sheet>
    );
}
